/**
 * React Query Client Configuration
 *
 * Why: Configure global React Query Settings
 * What: Sets default options for queries and mutations
 * Benefit: Consistent behaviour across all queries
 */

import { QueryClient } from "@tanstack/react-query";
import { logger } from "../utils";

/**
 * Create and configure React Query Client
 */

export const createQueryClient = (): QueryClient => {
  return new QueryClient({
    defaultOptions: {
      // Query defaults
      queries: {
        // Retry failed requests 2 times
        retry: 3,

        // Retry with exponential backoff
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Refetch on window focus in production
        refetchOnWindowFocus: import.meta.env.PROD,

        // Refetch on reconnect
        refetchOnReconnect: true,

        // Consider data stale after 5 minutes
        staleTime: 5 * 60 * 1000,

        // Keep unused data in cache for 10 minutes
        gcTime: 10 * 60 * 1000,

        // Throw errors to error boundries
        throwOnError: false,
      },

      // Mutation defaults
      mutations: {
        // Don't retry mutations by defaults
        retry: 0,

        // Error handling
        onError: (error) => {
          logger.error("Mutation error", error);
        },
      },
    },
  });
};

// Export singleton instance
export const queryClient = createQueryClient();
